import { useTranslations } from "next-intl";
import Image from "next/image";
import { useDateFormatter } from "react-aria";

import { TIMEZONE } from "@trip/constants";
import { cn } from "@trip/utils";

import { CalendarIcon } from "~/components/icons/calendar-icon";
import { UserIcon } from "~/components/icons/user-icon";
import { HR } from "~/components/typography";
import { buttonVariants } from "~/components/ui/button";
import { useCurrencyFormatter } from "~/lib/use-currency";
import { Link } from "~/navigation";

import { type TourFromPage } from "../page";

export function TourCard({
  date,
  amount,
  tour,
  totalCount,
}: {
  amount: number;
  date?: Date;
  totalCount: number;
  tour: Pick<
    TourFromPage,
    "heroImage" | "slug" | "id" | "location" | "title" | "tripadvisorRating"
  >;
}) {
  const formatUserCurrency = useCurrencyFormatter();
  const dateFormatter = useDateFormatter({
    dateStyle: "medium",
    timeZone: TIMEZONE,
  });
  const t = useTranslations("Checkout");
  return (
    <div>
      <div className="flex gap-4">
        <Image
          alt=""
          className="aspect-square size-[120px] shrink-0 overflow-hidden rounded-md object-cover"
          src={tour.heroImage.responsiveImage.src}
          unoptimized
          width={440}
          height={528}
        />
        <div className="min-w-0 space-y-1">
          <div className="mb-1 text-xs text-neutral-500">
            {tour.location?.name}
          </div>
          <h3 className="mb-2 truncate">{tour.title}</h3>
          <div className="grid grid-cols-[max-content_1fr] gap-x-0.5 gap-y-1 text-xs text-neutral-500">
            {date ? (
              <>
                <CalendarIcon className="size-4" />
                <div>{dateFormatter.format(date)}</div>
              </>
            ) : null}
            <UserIcon className="size-4" />
            <div>{t("travelersCount", { count: totalCount })}</div>
          </div>
          <div>
            <div className="font-medium">
              {t("approximateAmount", { amount: formatUserCurrency(amount) })}
            </div>
          </div>
        </div>
      </div>
      <div>
        <HR className="my-4" />
        <Link
          className={cn(buttonVariants({ variant: "sunset" }), "w-full")}
          href="/checkout"
        >
          {t("continueToCheckout")}
        </Link>
      </div>
    </div>
  );
}
